/// Pure value codecs for safely reading `unknown` when restoring list view state.
///
/// This module holds no POLICY (no Storage access, no screen keys, no restore
/// priority, no display labels) — those live in features/list-view-state. It
/// makes no decisions; it only accepts / normalizes values (FSD: shared).

import { isUuid } from '@shared/util/uuid';

/** Longest single string-array element accepted (drop pathologically long values). */
const MAX_STRING_ARRAY_ITEM_LENGTH = 120;
/** Largest unique count kept for a string array (guard against bloated input). */
const MAX_STRING_ARRAY_SIZE = 100;
export const MAX_LIST_VIEW_SEARCH_LENGTH = 200;

export function isPlainRecord(
  value: unknown,
): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/** Accept strings only; drop strings longer than `maxLength` when provided. */
export function readString(
  value: unknown,
  maxLength?: number,
): string | undefined {
  if (typeof value !== 'string') return undefined;
  if (maxLength !== undefined && value.length > maxLength) return undefined;
  return value;
}

export function readBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

export function readEnum<Value extends string>(
  value: unknown,
  allowed: readonly Value[],
): Value | undefined {
  return typeof value === 'string' && allowed.includes(value as Value)
    ? (value as Value)
    : undefined;
}

/**
 * Keep string elements only, deduped. Drop pathologically long elements
 * (over 120 chars) and cap the unique count at the first 100 (anti-corruption).
 */
export function readStringArray<Value extends string = string>(
  value: unknown,
  allowed?: readonly Value[],
): Value[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const unique = new Set<Value>();
  for (const item of value) {
    if (typeof item !== 'string' || item.length === 0) continue;
    if (item.length > MAX_STRING_ARRAY_ITEM_LENGTH) continue;
    if (allowed && !allowed.includes(item as Value)) continue;
    unique.add(item as Value);
    if (unique.size >= MAX_STRING_ARRAY_SIZE) break;
  }
  return [...unique];
}

export function readUuidArray(value: unknown): string[] | undefined {
  return readStringArray(value)?.filter(isUuid);
}

/** Accept positive integers only, clamped to `maximum`. */
export function readClampedPositiveInteger(
  value: unknown,
  maximum: number,
): number | undefined {
  if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
    return undefined;
  }
  return Math.min(value, maximum);
}

/** Accept integers inside `[min, max]` only (out of range -> undefined); no clamping. */
export function readIntegerInRange(
  value: unknown,
  min: number,
  max: number,
): number | undefined {
  if (
    typeof value !== 'number' ||
    !Number.isInteger(value) ||
    value < min ||
    value > max
  ) {
    return undefined;
  }
  return value;
}

export function compactRecord(
  values: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined),
  );
}
